import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderPlus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { fileStudy, listCollectionTargets, lookupStudy, unfileStudy } from '../../features/collections/api';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { useNavigate } from 'react-router-dom';
import {
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Study } from '../../data/types';
import { useStudyImageCount, useStudySeriesCount } from '../../hooks/useStudyThumbnail';
import { StudyThumbnail } from './StudyThumbnail';

interface Props {
  studies: Study[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  pageIndex: number;
  onPageIndexChange: (index: number) => void;
}

const PAGE_SIZE = 25;

const columns: ColumnDef<Study>[] = [
  {
    id: 'thumbnail',
    header: '',
    enableSorting: false,
    cell: ({ row }) => <StudyThumbnail studyInstanceUID={row.original.studyInstanceUID} />,
    meta: { width: 'w-16' },
  },
  {
    accessorKey: 'patientName',
    header: 'Patient',
    cell: ({ getValue }) => (
      <span className="font-medium">{(getValue<string>() || '(anonymous)').trim()}</span>
    ),
  },
  {
    accessorKey: 'patientId',
    header: 'Patient ID',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{getValue<string>() || '—'}</span>
    ),
  },
  {
    accessorKey: 'studyDate',
    header: 'Study date',
    cell: ({ getValue }) => getValue<string>() || '—',
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground">{getValue<string>() || '—'}</span>
    ),
  },
  {
    id: 'modalities',
    accessorFn: (s) => s.modalities.join(', '),
    header: 'Modalities',
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.modalities.length === 0
          ? '—'
          : row.original.modalities.map((m) => (
              <Badge key={m} variant="secondary">
                {m}
              </Badge>
            ))}
      </div>
    ),
  },
  {
    accessorKey: 'numberOfSeries',
    header: 'Series',
    cell: ({ row }) => <SeriesCount study={row.original} />,
    meta: { align: 'right' },
  },
  {
    accessorKey: 'numberOfInstances',
    header: 'Images',
    cell: ({ row }) => <ImageCount study={row.original} />,
    meta: { align: 'right' },
  },
  {
    id: 'file',
    header: '',
    cell: ({ row }) => <FileInto study={row.original} />,
    meta: { align: 'right' },
  },
];

/**
 * File this slide into a collection, from the worklist.
 *
 * SINGLE HOME: choosing a collection MOVES the slide rather than adding a second
 * membership, and "Unfiled" moves it back out. The menu stops at collections and
 * does not offer groups — placing into a specific group is what the collection's
 * own page is for, and a nested menu of every group in every collection is a
 * worse way to make the same choice.
 *
 * The collections are this workspace's own: slides do not move between
 * workspaces, they are copied there from the collection page. Somebody who only
 * views this workspace gets no menu at all: the server would refuse the filing.
 */
function FileInto({ study }: { study: Study }) {
  const qc = useQueryClient();
  const { current, ready } = useSelectedWorkspace();
  const workspace = current?.public_id;
  // THE ROLE decides, not the list. This comment always promised a viewer no
  // menu, but the only gate was "are there collections", and
  // /collections/targets answers that for any workspace the caller can READ —
  // so every viewer got the menu, and every pick a 'not found' alert. Asked
  // of the role the server's write targets are built from (scope.ts
  // targetsFrom drops exactly the viewers), and not fetched at all without it.
  const mayFile = current !== undefined && current.role !== 'viewer';
  const { data } = useQuery({
    queryKey: ['collection-targets', { workspace }],
    queryFn: () => listCollectionTargets(workspace),
    enabled: ready && mayFile,
  });
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['studies'] });
    void qc.invalidateQueries({ queryKey: ['collections'] });
    void qc.invalidateQueries({ queryKey: ['collection'] });
    void qc.invalidateQueries({ queryKey: ['collection-targets'] });
  };
  const file = useMutation({
    // The row is a QIDO result, named by UID; filing takes the public id, asked
    // of the workspace this list shows (a UID names a study per workspace).
    mutationFn: async (id: string | null) => {
      const { public_id } = await lookupStudy(study.studyInstanceUID, workspace);
      return id ? fileStudy(public_id, id) : unfileStudy(public_id);
    },
    onSuccess: done,
    // A refusal says why (slides still processing, the other workspace already
    // holding this study), and there is no room for it in a table cell.
    onError: (e) => window.alert((e as Error).message),
  });

  const collections = data?.collections ?? [];
  if (!mayFile || !collections.length) return null;

  return (
    <DropdownMenu>
      {/* stopPropagation: the row itself navigates to the study, and opening a
          menu must not also open the slide. */}
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="sm" title="File into a collection">
          <FolderPlus className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {collections.map((c) => (
          <DropdownMenuItem key={c.public_id} onSelect={() => file.mutate(c.public_id)}>
            {c.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => file.mutate(null)}>Unfiled</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Images" cell: viewable-image count (a whole WSI slide counts as one). Fetches
 *  the metadata it needs only once the row scrolls into view; renders empty until
 *  ready rather than flashing a number. */
/** True once the element has come near the viewport: counts are read lazily, row by row. */
function useSeenOnce(): [RefObject<HTMLSpanElement>, boolean] {
  const ref = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);
  return [ref, visible];
}

function ImageCount({ study }: { study: Study }) {
  const [ref, visible] = useSeenOnce();
  const { count, loading } = useStudyImageCount(study, visible);
  return <span ref={ref}>{loading ? '' : (count ?? '—')}</span>;
}

function SeriesCount({ study }: { study: Study }) {
  const [ref, visible] = useSeenOnce();
  const { count, loading } = useStudySeriesCount(study, visible);
  return <span ref={ref}>{loading ? '' : (count ?? '—')}</span>;
}

export function StudyTable({
  studies,
  sorting,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
}: Props) {
  const navigate = useNavigate();
  const pageSize = PAGE_SIZE;

  const table = useReactTable({
    data: studies,
    columns,
    state: { sorting, pagination: { pageIndex, pageSize } },
    onSortingChange,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      onPageIndexChange(next.pageIndex);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const total = studies.length;
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(total, (pageIndex + 1) * pageSize);

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-auto rounded-md border">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const meta = header.column.columnDef.meta as
                    | { width?: string; align?: string }
                    | undefined;
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(meta?.width, meta?.align === 'right' && 'text-right')}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === 'asc' ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => navigate(`/studies/${row.original.studyInstanceUID}`)}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as { align?: string } | undefined;
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(meta?.align === 'right' && 'text-right tabular-nums')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4 pt-3 text-sm text-muted-foreground">
        <span>
          {from}–{to} of {total}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          <span className="tabular-nums">
            Page {pageIndex + 1} of {table.getPageCount() || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
